<template>
  <form @submit.prevent="updateTwoFa" class="two-fa-pin">
    <div class="two-fa-pin__header">
      <div class="two-fa-pin__icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h3 class="two-fa-pin__title">Enter Verification Code</h3>
      <p class="two-fa-pin__desc">Enter the 6-digit code from your authenticator app</p>
    </div>
    <div ref="codeInput" class="two-fa-pin__inputs">
      <OnboardingCodeInput
        label="two fa code"
        type="text"
        :digit="digit.firstDigit"
        :error="errorOne || {}"
        :position="1"
        :disabled="loading"
        @input="setDigit"
        @handlePaste="handlePaste"
      />
      <OnboardingCodeInput
        label="two fa code"
        type="text"
        :digit="digit.secondDigit"
        :error="errorTwo || {}"
        :position="2"
        :disabled="loading"
        @input="setDigit"
        @handlePaste="handlePaste"
      />
      <OnboardingCodeInput
        label="two fa code"
        type="text"
        :digit="digit.thirdDigit"
        :error="errorThree || {}"
        :position="3"
        :disabled="loading"
        @input="setDigit"
        @handlePaste="handlePaste"
      />
      <OnboardingCodeInput
        label="two fa code"
        type="text"
        :digit="digit.forthDigit"
        :error="errorFour || {}"
        :position="4"
        :disabled="loading"
        @input="setDigit"
        @handlePaste="handlePaste"
      />
      <OnboardingCodeInput
        label="two fa code"
        type="text"
        :digit="digit.fifthDigit"
        :error="errorFive || {}"
        :position="5"
        :disabled="loading"
        @input="setDigit"
        @handlePaste="handlePaste"
      />
      <OnboardingCodeInput
        label="two fa code"
        type="text"
        :digit="digit.sixthDigit"
        :error="errorSix || {}"
        :position="6"
        :disabled="loading"
        @input="setDigit"
        @handlePaste="handlePaste"
      />
    </div>
    <OnboardingBtn
      class="two-fa-pin__btn"
      :loading="loading"
      :title="
        organisationDetails?.two_factor_enabled ? 'Deactivate 2FA' : 'Activate 2FA'
      "
    />
  </form>
</template>

<script>
import { mapState, mapActions } from "vuex";
import useValidate from "@vuelidate/core";
import { required, maxLength } from "@vuelidate/validators";
import OnboardingCodeInput from "@/components/onboarding/OnboardingCodeInput.vue";
import OnboardingBtn from "@/components/general/BtnComponent.vue";
import api from "@/utils/api.js";
import responseHandler from "@/utils/responseHandler.js"

export default {
  name: "TwoFaPin",

  emits: ["close"],

  components: {
    OnboardingCodeInput,
    OnboardingBtn,
  },

  data() {
    return {
      v$: useValidate(),
      loading: false,
      code: "",
      btnTitle: "",
      fields: [],
      fieldNum: 0,
      digit: {
        firstDigit: "",
        secondDigit: "",
        thirdDigit: "",
        forthDigit: "",
        fifthDigit: "",
        sixthDigit: "",
      },
      errorOne: {},
      errorTwo: {},
      errorThree: {},
      errorFour: {},
      errorFive: {},
      errorSix: {},
    };
  },

  validations() {
    return {
      digit: {
        firstDigit: { required, maxLength: maxLength(1) },
        secondDigit: { required, maxLength: maxLength(1) },
        thirdDigit: { required, maxLength: maxLength(1) },
        forthDigit: { required, maxLength: maxLength(1) },
        fifthDigit: { required, maxLength: maxLength(1) },
        sixthDigit: { required, maxLength: maxLength(1) },
      },
    };
  },

  computed: {
    ...mapState({
      organisationDetails: (state) =>
        state.organisationModule.organisationDetails,
        teamMember: (state) =>
        state.organisationModule.teamMember,
    }),
  },

  mounted() {
    this.fields = this.$refs.codeInput.querySelectorAll("input");
    this.fieldNum = this.fields.length;
  },

  methods: {
    ...mapActions("organisationModule", ["getOrganisationProfile", "updateTeamMemberInfo"]),

    handlePaste(value) {
      this.code = "";
      for (let index = 0; index < this.fieldNum; index++) {
        const payload = { digit: value[index], position: index + 1 };
        this.setDigit(payload);
      }
    },

    async updateTwoFa() {
      this.v$.$validate();
      const hasValue = Object.keys(this.digit).every((key) => this.digit[key]);

      if (this.v$.$errors.length === 0 && hasValue) {
        const payload = { token: this.code, type: "activate" };

        this.loading = true;
        try {
          const response = await api.updateTwoFa(payload);
          if (response) {
            await this.getOrganisationProfile();

            if (this.teamMember) { 
              await this.updateTeamMemberInfo("activate");
            }
          }

          // if (this.teamMember) {
          //     await this.updateTeamMemberInfo("activate");
          //   } else {
          //     await api.updateTwoFa(payload);

          //     await this.getOrganisationProfile();
          //   }
          this.$emit("close");
        } catch (error) {
          console.error("Error updating two-factor authentication:", error);

          responseHandler.handleError(error, { skipLogout: true });
        } finally {
          this.loading = false;
        }
      } else {
        this.v$.$errors.map((error) => {
          if (error.$property === "firstDigit") this.errorOne = error;
          if (error.$property === "secondDigit") this.errorTwo = error;
          if (error.$property === "thirdDigit") this.errorThree = error;
          if (error.$property === "forthDigit") this.errorFour = error;
          if (error.$property === "fifthDigit") this.errorFive = error;
          if (error.$property === "sixthDigit") this.errorSix = error;
        });
      }
    },
    
    setDigit(payload) {
      const keyCode = payload.event ? payload.event.inputType : null;
      switch (payload.position) {
        case 1:
          this.digit.firstDigit = payload.digit;
          this.v$.digit.firstDigit.$touch();
          this.errorOne = this.v$.$errors[0];
          this.concatDigits();
          if (keyCode === "deleteContentBackward") {
            return;
          } else {
            this.fields[payload.position].focus();
          }
          break;
        case 2:
          this.digit.secondDigit = payload.digit;
          this.v$.digit.secondDigit.$touch();
          this.errorTwo = this.v$.$errors[0];
          this.concatDigits();
          if (keyCode === "deleteContentBackward") {
            this.fields[payload.position - 2].focus();
            this.fields[payload.position - 2].select();
          } else {
            this.fields[payload.position].focus();
          }
          break;
        case 3:
          this.digit.thirdDigit = payload.digit;
          this.v$.digit.thirdDigit.$touch();
          this.errorThree = this.v$.$errors[0];
          this.concatDigits();
          if (keyCode === "deleteContentBackward") {
            this.fields[payload.position - 2].focus();
            this.fields[payload.position - 2].select();
          } else {
            this.fields[payload.position].focus();
          }
          break;
        case 4:
          this.digit.forthDigit = payload.digit;
          this.v$.digit.forthDigit.$touch();
          this.errorFour = this.v$.$errors[0];
          this.concatDigits();
          if (keyCode === "deleteContentBackward") {
            this.fields[payload.position - 2].focus();
            this.fields[payload.position - 2].select();
          } else {
            this.fields[payload.position].focus();
          }
          break;
        case 5:
          this.digit.fifthDigit = payload.digit;
          this.v$.digit.fifthDigit.$touch();
          this.errorFive = this.v$.$errors[0];
          this.concatDigits();
          if (keyCode === "deleteContentBackward") {
            this.fields[payload.position - 2].focus();
            this.fields[payload.position - 2].select();
          } else {
            this.fields[payload.position].focus();
          }
          break;
        case 6:
          this.digit.sixthDigit = payload.digit;
          this.v$.digit.sixthDigit.$touch();
          this.errorSix = this.v$.$errors[0];
          this.concatDigits();
          if (keyCode === "deleteContentBackward") {
            this.fields[payload.position - 2].focus();
            this.fields[payload.position - 2].select();
          }
          break;

        default:
          break;
      }
    },

    concatDigits() {
      this.code = this.digit.firstDigit
        .concat(this.digit.secondDigit)
        .concat(this.digit.thirdDigit)
        .concat(this.digit.forthDigit)
        .concat(this.digit.fifthDigit)
        .concat(this.digit.sixthDigit);
    },
  },

  watch: {
    success(newValue) {
      deep: true;
      immediate: true;
      if (newValue === true) {
        this.digit = {
          firstDigit: "",
          secondDigit: "",
          thirdDigit: "",
          forthDigit: "",
          fifthDigit: "",
          sixthDigit: "",
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.two-fa-pin {
  width: 100%;
  padding: 0 24px;

  &__header {
    text-align: center;
    margin-bottom: 32px;
  }

  &__icon {
    width: 56px;
    height: 56px;
    background: #F1F5F9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    color: #4D84FF;
  }

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 8px 0;
  }

  &__desc {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__inputs {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-bottom: 32px;
  }

  &__btn {
    width: 100%;
    max-width: 280px;
    margin: 0 auto;
    display: block;
  }
}
</style>
